import React, { createContext, useContext, useMemo } from 'react';
import { AuthProviderContent, IAuthProvider } from './interfaces';
import {
  CURRENT_USER_QUERY_KEY,
  useGetCurrentMember,
  useLogin,
  useLogout,
} from '../../reactQuery/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useSyncMemberLocale } from '@hooks/useSyncMemberLocale';

export const AuthContext = createContext<AuthProviderContent>({
  currentUser: null,
  handleLogin: async () => false,
  handleLogout: async () => Promise.resolve(),
  isLoading: false,
  clearCurrentUser: (): void => {},
});

export const AuthProvider: React.FC<IAuthProvider> = ({ children }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: currentUser, isLoading: isLoadingCurrentUser } =
    useGetCurrentMember();

  useSyncMemberLocale(currentUser ?? null);
  const loginMutation = useLogin();
  const logoutMutation = useLogout();

  const handleLogin = async (email: string, password: string) => {
    try {
      await loginMutation.mutateAsync({
        email,
        password,
      });

      return true;
    } catch (_error) {
      return false;
    }
  };

  const handleLogout = async (): Promise<void> => {
    await logoutMutation.mutateAsync();

    clearCurrentUser();
  };

  const clearCurrentUser = () => {
    if (isLoadingCurrentUser) return;

    queryClient.setQueryData(CURRENT_USER_QUERY_KEY, null);
    queryClient.removeQueries({
      queryKey: CURRENT_USER_QUERY_KEY,
    });

    navigate('/login', { replace: true });
  };

  const value = useMemo(
    () => ({
      currentUser: currentUser ?? null,
      handleLogout,
      handleLogin,
      clearCurrentUser,
      isLoading: isLoadingCurrentUser,
    }),
    [currentUser, isLoadingCurrentUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
